import { useMemo } from 'react';
import {
  calcComponentCncCost,
  type ComponentCncResult,
  type ComponentCncSettings,
  type ComponentNestingPart,
  type MaterialLibrary,
} from '@estimator/shared';
import { useCompanySettings } from '@/api/company-settings';

/**
 * Priced CNC cost for one unit of a component, at the company's reference
 * batch quantity.
 *
 * Deliberately independent of the quantity selector in ComponentNestingPanel:
 * a price has to be reproducible, so it must not depend on which button was
 * clicked last. The panel computes its own diagnostic figure separately.
 */
export function useComponentCncCost(
  parts: ComponentNestingPart[],
  materials: MaterialLibrary[],
): ComponentCncResult {
  const { data: companySettings } = useCompanySettings();

  const materialMap = useMemo(
    () => new Map(materials.map((m) => [m.id, m])),
    [materials],
  );

  return useMemo(
    () => calcComponentCncCost(
      parts,
      materialMap,
      companySettings as ComponentCncSettings | undefined,
      companySettings?.kerf_width,
    ),
    [parts, materialMap, companySettings],
  );
}
